import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { authApi } from '../../api/endpoints';
import { errorMessage, fieldErrors, issueMessage, issuesOf, requestIdOf } from '../../api/errors';
import { queryKeys } from '../../api/queries';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { Callout } from '../../ui/Feedback';
import { Code, useDocumentTitle } from '../../ui/Layout';
import { TextField } from '../../ui/TextField';
import { AuthLayout, authStyles as styles } from './AuthLayout';

export function RegisterPage() {
  const { t, locale } = useI18n();
  useDocumentTitle(t.auth.registerTitle);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const register = useMutation({
    mutationFn: () =>
      authApi.register({ displayName: displayName.trim(), email: email.trim(), password, locale }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['org'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      await navigate({ to: '/orgs/new' });
    },
  });

  const FIELDS = ['displayName', 'email', 'password'];
  const allIssues = issuesOf(register.error);
  const issues = fieldErrors(t, allIssues);
  const otherIssues = allIssues.filter((issue) => !FIELDS.includes(issue.path));
  const showCallout = Boolean(register.error) && (allIssues.length === 0 || otherIssues.length > 0);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    register.mutate();
  };

  return (
    <AuthLayout>
      <form className={styles.form} onSubmit={onSubmit} noValidate aria-labelledby="register-title">
        <div className={styles.formHeader}>
          <h1 id="register-title" className={styles.formTitle}>
            {t.auth.registerTitle}
          </h1>
          <p className={styles.formSubtitle}>{t.auth.registerSubtitle}</p>
        </div>
        {showCallout ? (
          <Callout tone="danger" role="alert" title={errorMessage(t, register.error)}>
            {otherIssues.map((issue) => (
              <p key={`${issue.path}:${issue.issue}`}>{issueMessage(t, issue)}</p>
            ))}
            {requestIdOf(register.error) ? (
              <span>
                {t.errors.reference}: <Code>{requestIdOf(register.error)}</Code>
              </span>
            ) : null}
          </Callout>
        ) : null}
        <TextField
          label={t.auth.displayName}
          autoComplete="name"
          value={displayName}
          onChange={setDisplayName}
          errorMessage={issues.displayName}
          isRequired
          autoFocus
        />
        <TextField
          label={t.auth.email}
          type="email"
          autoComplete="email"
          dir="ltr"
          value={email}
          onChange={setEmail}
          errorMessage={issues.email}
          isRequired
        />
        <TextField
          label={t.auth.password}
          type="password"
          autoComplete="new-password"
          dir="ltr"
          value={password}
          onChange={setPassword}
          description={t.auth.passwordHint}
          errorMessage={issues.password}
          isRequired
        />
        <Button
          type="submit"
          variant="primary"
          className={styles.submit}
          isPending={register.isPending}
          icon={<UserPlus size={16} aria-hidden="true" />}
        >
          {t.auth.register}
        </Button>
        <p className={styles.switch}>
          {t.auth.haveAccount} <Link to="/login">{t.auth.signIn}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
